import React, { useState, useEffect } from 'react';
import { adminAPI, useAdminAuth } from '@/contexts/AdminAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useModal } from '@/contexts/ModalContext';
import { toast } from '@/components/ui/sonner';
import {
  UserCheck,
  Shield,
  Users,
  Plus,
  Trash2,
  Crown,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Search
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import UserSearchInput from '@/components/admin/UserSearchInput';

interface Admin {
  _id: string;
  firebaseUid: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  usage: {
    lastActivity?: string;
  };
}

interface User {
  id: string;
  uid: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  isBlocked: boolean;
}

const AdminAdminsPage: React.FC = () => {
  const { user, isRootAdmin } = useAdminAuth();
  const { showConfirmModal } = useModal();
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  // Add admin form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAdminData, setNewAdminData] = useState({
    email: ''
  });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    if (isRootAdmin) {
      loadAdmins();
    }
  }, [isRootAdmin]);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminAPI.getAdmins();
      setAdmins(response.data.admins);
    } catch (error: any) {
      setError(error.message || 'Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSearchChange = (value: string) => {
    setNewAdminData(prev => ({ ...prev, email: value }));
    // Clear selected user if email is manually changed
    if (selectedUser && selectedUser.email !== value) {
      setSelectedUser(null);
    }
  };

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    setNewAdminData(prev => ({ ...prev, email: user.email }));
    setError(''); // Clear any previous errors
  };

  const handlePromoteUser = async () => {
    if (!newAdminData.email) {
      setError('Please select a user to promote');
      return;
    }

    // Check if selected user is eligible for promotion
    if (selectedUser) {
      if (selectedUser.role === 'admin' || selectedUser.role === 'root_admin') {
        setError('User is already an admin');
        return;
      }
      if (selectedUser.isBlocked) {
        setError('Cannot promote blocked user');
        return;
      }
      if (!selectedUser.isActive) {
        setError('Cannot promote inactive user');
        return;
      }
    }

    // Show confirmation modal
    const confirmed = await showConfirmModal({
      title: 'Promote to Admin',
      description: 'Are you sure you want to promote this user to admin?',
      variant: 'default',
      confirmText: 'Confirm',
      cancelText: 'Cancel'
    });

    if (!confirmed) {
      return;
    }

    try {
      setActionLoading('promote');
      setError('');
      setSuccess('');

      await adminAPI.promoteToAdmin(newAdminData.email);
      toast.success(`User ${selectedUser?.displayName || newAdminData.email} promoted to admin successfully`);
      setNewAdminData({ email: '' });
      setSelectedUser(null);
      setShowAddForm(false);
      await loadAdmins();
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to promote user';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDemoteAdmin = async (adminEmail: string) => {
    if (adminEmail === user?.email) {
      setError('You cannot demote yourself');
      return;
    }

    // Show confirmation modal
    const confirmed = await showConfirmModal({
      title: 'Demote Admin',
      description: 'This will remove admin privileges. Proceed?',
      variant: 'warning',
      confirmText: 'Confirm',
      cancelText: 'Cancel'
    });

    if (!confirmed) {
      return;
    }

    try {
      setActionLoading(`demote-${adminEmail}`);
      setError('');
      setSuccess('');

      await adminAPI.demoteAdmin(adminEmail);
      toast.success('Admin demoted successfully');
      await loadAdmins();
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to demote admin';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const getRoleBadge = (role: string) => {
    if (role === 'root_admin') {
      return (
        <Badge className="bg-purple-100 text-purple-800 flex items-center gap-1">
          <Crown className="h-3 w-3" />
          Root Admin
        </Badge>
      );
    }
    
    return (
      <Badge className="bg-blue-100 text-blue-800 flex items-center gap-1">
        <Shield className="h-3 w-3" />
        Admin
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Only root admin can access this page
  if (!isRootAdmin) {
    return (
      <div className="space-y-6">
        <Alert className="bg-red-50 border-red-200">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-red-800">
            Access denied. Only root administrators can manage other admins.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Admin Management</h1>
        </div>
        <div className="grid grid-cols-1 gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <UserCheck className="h-6 w-6" />
            Admin Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage administrator accounts and permissions
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={loadAdmins}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => {
              setShowAddForm(!showAddForm);
              if (!showAddForm) {
                // Clear form when opening
                setNewAdminData({ email: '' });
                setSelectedUser(null);
                setError('');
                setSuccess('');
              }
            }}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Admin
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <Alert className="bg-red-50 border-red-200">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-red-800">
            {error}
          </AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription className="text-green-800">
            {success}
          </AlertDescription>
        </Alert>
      )}

      {/* Add Admin Form */}
      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>Promote User to Admin</CardTitle>
            <CardDescription>
              Promote an existing user to administrator role
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">
                  Search User *
                </label>
                <UserSearchInput
                  value={newAdminData.email}
                  onChange={handleUserSearchChange}
                  onUserSelect={handleUserSelect}
                  placeholder="Search users by email or name..."
                  excludeRoles={['admin', 'root_admin']}
                  className="bg-background border-border text-foreground"
                  disabled={actionLoading === 'promote'}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Search and select an existing user to promote to admin. Only regular users can be promoted.
                </p>

                {/* Selected User Preview */}
                {selectedUser && (
                  <div className="mt-3 p-3 bg-accent/20 border border-border rounded-md">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <Users className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium text-sm">{selectedUser.displayName || 'No name'}</div>
                          <div className="text-xs text-muted-foreground">{selectedUser.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge className="text-xs bg-blue-100 text-blue-800 border-blue-200">
                          {selectedUser.role}
                        </Badge>
                        {selectedUser.isActive && (
                          <Badge className="text-xs bg-green-100 text-green-800 border-green-200">
                            Active
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2 mt-6">
              <Button
                onClick={handlePromoteUser}
                disabled={actionLoading === 'promote' || !newAdminData.email}
                className="bg-primary hover:bg-primary/90"
              >
                {actionLoading === 'promote' ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Shield className="h-4 w-4 mr-2" />
                )}
                Promote to Admin
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddForm(false);
                  setNewAdminData({ email: '' });
                  setSelectedUser(null);
                  setError('');
                }}
                className="border-border text-foreground hover:bg-accent/10"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admins List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Current Administrators ({admins.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {admins.map((admin) => (
              <div key={admin._id} className="border border-border rounded-lg p-4 bg-card hover:bg-accent/5">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-600">
                          {(admin.displayName || admin.email).charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-foreground">
                          {admin.displayName || admin.email.split('@')[0]}
                        </div>
                        <div className="text-sm text-muted-foreground">{admin.email}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                      <span>UID: {admin.firebaseUid}</span>
                      <span>Joined: {formatDate(admin.createdAt)}</span>
                      {admin.usage?.lastActivity && (
                        <span>Last active: {formatDate(admin.usage.lastActivity)}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    {getRoleBadge(admin.role)}
                    
                    <div className="flex items-center space-x-2">
                      {admin.role !== 'root_admin' && admin.email !== user?.email && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDemoteAdmin(admin.email)}
                              disabled={actionLoading === `demote-${admin.email}`}
                            >
                              {actionLoading === `demote-${admin.email}` ? (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                              ) : (
                                <Users className="h-4 w-4" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Demote to User</p>
                          </TooltipContent>
                        </Tooltip>
                      )}

                      {admin.email === user?.email && (
                        <Badge className="bg-green-100 text-green-800">
                          You
                        </Badge>
                      )}

                      {admin.role === 'root_admin' && (
                        <Badge className="bg-accent/20 text-accent border border-accent/30">
                          Protected
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {admins.length === 0 && !loading && (
              <div className="text-center py-8 text-muted-foreground">
                No administrators found
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Admin Permissions Info */}
      <Card>
        <CardHeader>
          <CardTitle>Admin Permissions</CardTitle>
          <CardDescription>
            Overview of what different admin roles can do
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
                <Crown className="h-4 w-4 text-purple-600" />
                Root Admin
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Manage all users and admins</li>
                <li>• Access system settings</li>
                <li>• Toggle maintenance mode</li>
                <li>• Update user limits</li>
                <li>• Reset user counters</li>
                <li>• View all analytics and logs</li>
                <li>• Manage support requests</li>
                <li>• Cannot be demoted or deleted</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
                <Shield className="h-4 w-4 text-blue-600" />
                Regular Admin
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Manage regular users</li>
                <li>• View analytics and logs</li>
                <li>• Manage support requests</li>
                <li>• Block/unblock users</li>
                <li>• View user details</li>
                <li>• Cannot manage other admins</li>
                <li>• Cannot access system settings</li>
                <li>• Can be demoted by root admin</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </TooltipProvider>
  );
};

export default AdminAdminsPage;
